import {useState} from "react";
import {
	GridPrintExportMenuItem,
	GridToolbarColumnsButton,
	GridToolbarContainer,
	GridToolbarDensitySelector,
	GridToolbarExportContainer,
	GridToolbarFilterButton,
	type GridColDef,
	type GridValidRowModel,
	useGridApiContext
} from "@mui/x-data-grid-pro";
import MenuItem from "@mui/material/MenuItem";
import {
	buildGridCsvBlob,
	dedupeGridRowsById,
	downloadCsvBlob,
	type GridCsvBlobOptions
} from "@/app2/shared/dataGrid/gridCsvExport";

export type DataGridSelectionCsvToolbarProps<R extends GridValidRowModel = GridValidRowModel> = {
	/**
	 * Accumulated selected row models (e.g. cross-page server-side selection).
	 * When empty, CSV uses the grid’s default export (typically current page).
	 */
	selectedRows: R[];
	/** When true (default), dedupe by row `id`, first occurrence wins. */
	dedupeSelection?: boolean;
	/** Passed through to {@link buildGridCsvBlob}. */
	csvBlobOptions?: GridCsvBlobOptions;
	/** Base name for the downloaded file (sanitized). Defaults to `document.title`, then `export`. */
	downloadBaseName?: string;
	onCsvExportError?: (error: unknown) => void;
};

type MenuItemHideProps = {hideMenu?: () => void};

function SelectionCsvMenuItem<R extends GridValidRowModel>(
	props: DataGridSelectionCsvToolbarProps<R> & MenuItemHideProps
) {
	const {
		hideMenu,
		selectedRows,
		dedupeSelection = true,
		csvBlobOptions,
		downloadBaseName,
		onCsvExportError
	} = props;
	const apiRef = useGridApiContext();
	const [exporting, setExporting] = useState(false);

	const onClick = async () => {
		const rows = dedupeSelection ? dedupeGridRowsById(selectedRows) : [...selectedRows];
		if (rows.length === 0) {
			apiRef.current.exportDataAsCsv();
			hideMenu?.();
			return;
		}
		setExporting(true);
		try {
			const visibleColumns = apiRef.current.getVisibleColumns() as GridColDef<R>[];
			const blob = buildGridCsvBlob(rows, visibleColumns, csvBlobOptions ?? {utf8WithBom: true});
			const title =
				downloadBaseName?.trim() ||
				(typeof document !== "undefined" ? document.title.trim() : "") ||
				"export";
			downloadCsvBlob(blob, title, {defaultBaseName: "export"});
		} catch (e) {
			onCsvExportError?.(e);
			console.error("Data grid CSV export failed", e);
		} finally {
			setExporting(false);
			hideMenu?.();
		}
	};

	return (
		<MenuItem onClick={onClick} disabled={exporting}>
			{apiRef.current.getLocaleText("toolbarExportCSV")}
		</MenuItem>
	);
}

/**
 * Standard admin toolbar (columns, filter, density, export) with CSV export that uses
 * `selectedRows` when provided (full row models for server-side / cross-page selection).
 */
export function DataGridSelectionCsvToolbar<R extends GridValidRowModel = GridValidRowModel>(
	props: DataGridSelectionCsvToolbarProps<R>
) {
	return (
		<GridToolbarContainer>
			<GridToolbarColumnsButton
				placeholder={undefined}
				onPointerEnterCapture={undefined}
				onPointerLeaveCapture={undefined}
				onResize={undefined}
				onResizeCapture={undefined}
			/>
			<GridToolbarFilterButton {...({} as any)} />
			<GridToolbarDensitySelector
				placeholder={undefined}
				onPointerEnterCapture={undefined}
				onPointerLeaveCapture={undefined}
				onResize={undefined}
				onResizeCapture={undefined}
			/>
			<GridToolbarExportContainer
				placeholder={undefined}
				onPointerEnterCapture={undefined}
				onPointerLeaveCapture={undefined}
				onResize={undefined}
				onResizeCapture={undefined}
			>
				<SelectionCsvMenuItem<R> {...props} />
				<GridPrintExportMenuItem />
			</GridToolbarExportContainer>
		</GridToolbarContainer>
	);
}
